import { AuthUserInfo } from '../redux/auth/types';
import { EmailGuestShare, JobLog } from './jobLog';
import { useSelector } from 'react-redux';
import { selectAuthUserInfo } from '../redux/auth/selectors';
import { Roles } from '@dataquiver/stoneage-roles';
import { AnnotationViewModel } from '../components/annotations/AnnotationsContext';
import { Bundle } from './bundle';
import { JobInfoJobLog } from '../pages/job-info/types';

function isLegacyShared(user: AuthUserInfo, log: JobLog, permission: string) {
  return log.share?.org === user.org && log.share?.[permission];
}

function isSharedWithUser(user: AuthUserInfo, share: EmailGuestShare) {
  return share.emailAddress === user.email && share.expiresOn > Date.now();
}

export function canSeeTubeGraph(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'tubes') || // legacy share check
    log.org === user.org || // or user belongs to same org as log
    log.permissions?.some(p => p.principal === user.org && p.permissions.readTubes) || // or user is in org that has permission to read tubes
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readTubes)
  );
}

export function canSeeTimestamps(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'timestamps') || // legacy share check
    log.org === user.org || // or user belongs to same org as log
    log.permissions?.some(p => p.principal === user.org && p.permissions.readTimestamps) || // or user is in org that has permission to read timestamps
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readTimestamps)
  );
}

export function canSeePressures(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'pressures') || // legacy share check
    log.org === user.org || // or user belongs to same org as log
    log.permissions?.some(p => p.principal === user.org && p.permissions.readPressures) || // or user is in org that has permission to read pressures
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readPressures)
  );
}

export function canSeePieCharts(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'pies') ||
    log.org === user.org ||
    log.permissions?.some(p => p.principal === user.org && p.permissions.readPies) ||
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readPies)
  );
}

export function canSeeIdleVsPressure(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'idleVsPressure') ||
    log.org === user.org ||
    log.permissions?.some(p => p.principal === user.org && p.permissions.readIdleVsPressure) ||
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readIdleVsPressure)
  );
}

export function canSeeAnnotations(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  return (
    isLegacyShared(user, log, 'annotations') || // legacy share check
    log.org === user.org || // or user belongs to same org as log
    log.permissions?.some(p => p.principal === user.org && p.permissions.readAnnotations) || // or user is in org that has permission to read readAnnotations
    log.emailGuestShares?.some(s => isSharedWithUser(user, s) && s.permissions.readAnnotations)
  );
}

export function canShare(user: AuthUserInfo, log: JobInfoJobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  if (Roles.isUser(user.role, { isExact: true })) return false;

  // Can only share or unshare if the job belongs to the users own organization.
  return log.org === user.org;
}

// Guest cannot see shares, but everyone else can.
export function canSeeShares(user: AuthUserInfo, log: JobInfoJobLog) {
  return !Roles.isJobReportGuest(user.role);
}

export function canDelete(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;

  // Can only delete if the job belongs to the users own organization.
  return log.org === user.org;
}

export function canManageAnnotations(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;
  return log.org === user.org;
}

export function canDownloadLog(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAgeDev(user.role)) return true;
  return log.org === user.org;
}

export function canPrintReport(user: AuthUserInfo, log: JobLog): boolean {
  return !Roles.isJobReportGuest(user.role);
}

export function canShowAnnotationUser(user: AuthUserInfo, log: JobLog) {
  if (Roles.isStoneAge(user.role)) return true;
  return log.org === user.org;
}

export function canEditAnnotation(user: AuthUserInfo, annotation: AnnotationViewModel) {
  return !annotation.deletedOn && (Roles.isManager(user.role) || annotation.createdBy === user.id);
}

export function canDeleteAnnotation(user: AuthUserInfo, annotation: AnnotationViewModel) {
  return !annotation.deletedOn && (Roles.isManager(user?.role) || annotation.createdBy === user?.id);
}

export function canDeleteBundle(user: AuthUserInfo, bundle: Bundle) {
  // Users are not allowed to delete bundles all other roles can delete bundles
  // within their organization
  if (Roles.isUser(user.role, { isExact: true })) return false;

  return bundle.organizationId === user.org;
}

export function canShareBundle(user: AuthUserInfo, bundle: Bundle) {
  // Users are not allowed to share bundles
  if (Roles.isUser(user.role, { isExact: true })) return false;

  //SA Devs can share bundles across organizations
  if (Roles.isStoneAgeDev(user.role, { isExact: true })) return true;

  //All other roles can only share bundles within their organization
  return bundle.organizationId === user.org;
}
/**
 * Defines a policy evaluator for a resource. It will be passed the current user and the requested resource
 */
export type PolicyFn<TResource> = (user: AuthUserInfo, resource: TResource) => boolean;

/**
 * A hook that can help you easily determine if the user can access a given feature by evaluating a policy against
 * the current user and resource
 *
 * @example
 * const canSeeAnnotations = usePolicy(canSeeAnnotations, log);
 *
 * @param policy the policy to evaluate
 * @param resource the resource to evaluate the policy against
 */
export function usePolicy<T>(policy: PolicyFn<T>, resource?: T | null) {
  const userData = useSelector(selectAuthUserInfo);
  if (!resource || !userData) return false;

  return policy(userData, resource);
}
